/**
 * Constants for SmartChoiceDemo component
 */

// Storage key for persisting demo settings in localStorage
export const STORAGE_KEY = 'smart-choice-demo-settings';

// Default settings for the demo
export const defaultSettings = {
  isSuperUser: true,
  isFullScreenCompareSmartChoice: false,
  isMixedResultsPage: false,
  showChoosePCPHeader: false,
  showMemberSelection: true,
  memberId: '',
  portalName: 'uhc',
  theme: 'uhc',
  language: 'en-US',
  basePath: '/connect',
  useFallbackLob: false,
  // Mock recommendation preferences state
  mockPreferences: {
    hasDismissedCoachMark: false,
    hasDismissedCoachMarkWeb: false,
    hasDismissedPopover: false,
    hasSetPreferences: false,
    hasDefaultPreferences: false,
  },
};

// Type for mock preferences
export interface MockPreferences {
  hasDismissedCoachMark: boolean;
  hasDismissedCoachMarkWeb: boolean;
  hasDismissedPopover: boolean;
  hasSetPreferences: boolean;
  hasDefaultPreferences: boolean;
}

// Type for demo settings
export interface DemoSettings {
  isSuperUser: boolean;
  isFullScreenCompareSmartChoice: boolean;
  isMixedResultsPage: boolean;
  showChoosePCPHeader: boolean;
  showMemberSelection: boolean;
  memberId: string;
  portalName: string;
  theme: string;
  language: string;
  basePath: string;
  useFallbackLob: boolean;
  mockPreferences: MockPreferences;
}

// Preset configuration type
export interface MockPreset {
  label: string;
  description: string;
  preferences: MockPreferences;
}

// Preset configurations for quick testing
export const mockPresets: Record<string, MockPreset> = {
  freshUser: {
    label: '🆕 Fresh User',
    description: 'New user who has never interacted with Smart Choice',
    preferences: {
      hasDismissedCoachMark: false,
      hasDismissedCoachMarkWeb: false,
      hasDismissedPopover: false,
      hasSetPreferences: false,
      hasDefaultPreferences: false,
    },
  },
  seenCoachmark: {
    label: '👀 Seen Coachmark',
    description: 'User has seen and dismissed the coachmark',
    preferences: {
      hasDismissedCoachMark: true,
      hasDismissedCoachMarkWeb: true,
      hasDismissedPopover: false,
      hasSetPreferences: false,
      hasDefaultPreferences: false,
    },
  },
  hasPreferences: {
    label: '⚙️ Has Preferences',
    description: 'User has set their preferences',
    preferences: {
      hasDismissedCoachMark: true,
      hasDismissedCoachMarkWeb: true,
      hasDismissedPopover: true,
      hasSetPreferences: true,
      hasDefaultPreferences: false,
    },
  },
  defaultPrefs: {
    label: '📋 Default Prefs',
    description: 'User has default (null/no) preferences set',
    preferences: {
      hasDismissedCoachMark: true,
      hasDismissedCoachMarkWeb: true,
      hasDismissedPopover: true,
      hasSetPreferences: true,
      hasDefaultPreferences: true,
    },
  },
  dismissedAll: {
    label: '✅ Dismissed All',
    description: 'User has dismissed all prompts and popovers',
    preferences: {
      hasDismissedCoachMark: true,
      hasDismissedCoachMarkWeb: true,
      hasDismissedPopover: true,
      hasSetPreferences: true,
      hasDefaultPreferences: true,
    },
  },
};

// Widget prop descriptions for helper text
export const widgetPropDescriptions = {
  isSuperUser:
    '⚠️ This should always be true for demo purposes as it enables super user features and audit data tracking for internal users.',
  isFullScreenCompareSmartChoice:
    '⚠️ Hides widget link UI for full-page compare mode.',
  isMixedResultsPage: 'Widget is on mixed results page.',
  showChoosePCPHeader: 'Shows "Choose PCP" header.',
  showMemberSelection: 'Shows member selection options.',
  memberId:
    'Override member ID for preference storage. Falls back to authenticated user ID.',
  showDetailsPage:
    'When enabled, opening the widget will show the smartchoice details drawer instead of the setting controls.',
};

// Mock preference descriptions
export const mockPreferenceDescriptions: Record<keyof MockPreferences, string> =
  {
    hasDismissedCoachMark: 'Mobile coachmark dismissed.',
    hasDismissedCoachMarkWeb: 'Web coachmark dismissed.',
    hasDismissedPopover: 'Popover dismissed.',
    hasSetPreferences: 'User has set preferences.',
    hasDefaultPreferences: 'No preferences set, using default.',
  };

// Loading state types - only 'preferences' shows skeleton for widgets
export type LoadingStateType = 'none' | 'preferences';

export interface LoadingStateConfig {
  label: string;
  description: string;
  skeletonHeight: number;
  skeletonWidth: number;
  source: string;
}

// Loading state configurations for skeleton debugging
export const loadingStateConfigs: Record<LoadingStateType, LoadingStateConfig> =
  {
    none: {
      label: '✅ None',
      description: 'Widget renders normally',
      skeletonHeight: 0,
      skeletonWidth: 0,
      source: 'N/A',
    },
    preferences: {
      label: '⚙️ Preferences',
      description:
        'Preferences API loading skeleton (only skeleton shown for widgets)',
      skeletonHeight: 20,
      skeletonWidth: 100,
      source: 'SmartChoiceWidget.tsx l.447',
    },
  };
